import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
    Box, Button, Flex, Heading, Text, SimpleGrid, Spinner, useToast,
    VStack, HStack, Badge, Divider, Stat, StatLabel, StatNumber, StatHelpText,
} from "@chakra-ui/react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { fireMembershipConversion } from "../utils/tenantConversions";

/**
 * MembershipPage — customer-facing enrollment + balance for prepaid pounds
 * subscription plans. Serves the SECONDARY "standalone door" (its own route),
 * and the same component powers the in-booking-flow PRIMARY offer via the
 * embeddable <MembershipEnroll> below.
 *
 * Flow: pick plan -> save card (SetupIntent) -> enroll (charges first month,
 * credits allotment). Active members see their balance + recent activity and a
 * cancel action instead of the offer.
 */
const API = process.env.REACT_APP_AWS_API_URL;

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const lb = (v) => `${Number(v || 0)} lb`;

// ── Enrollment form (card capture + plan pick). Rendered inside <Elements>. ──
export const MembershipEnroll = ({
    laundryId, customerId, plans, onEnrolled, compact = false,
}) => {
    const toast = useToast();
    const stripe = useStripe();
    const elements = useElements();
    const authToken = localStorage.getItem("idToken");
    const [selectedId, setSelectedId] = useState(plans?.[0]?.planId || plans?.[0]?.plan_id || null);
    const [submitting, setSubmitting] = useState(false);

    const selected = (plans || []).find((p) => (p.planId || p.plan_id) === selectedId);

    const handleEnroll = async () => {
        if (!selected) {
            toast({ title: "Pick a plan first", status: "warning", duration: 2500 });
            return;
        }
        if (!stripe || !elements) return;
        setSubmitting(true);
        try {
            // 1) SetupIntent to save the card off-session.
            const si = await axios.post(`${API}/api/membership/setup-intent`,
                { customerId, laundryId }, { headers: { "x-api-key": authToken } });
            if (si.data?.status !== "success" || !si.data?.clientSecret) {
                throw new Error(si.data?.message || "Could not start card setup");
            }
            const card = elements.getElement(CardElement);
            const confirm = await stripe.confirmCardSetup(si.data.clientSecret, {
                payment_method: { card },
            });
            if (confirm.error) {
                throw new Error(confirm.error.message || "Card could not be saved");
            }
            // 2) Enroll (server charges first month + credits allotment).
            const res = await axios.post(`${API}/api/membership/enroll`,
                { customerId, laundryId, planId: selectedId },
                { headers: { "x-api-key": authToken } });
            if (res.data?.status !== "success") {
                throw new Error(res.data?.message || "Enrollment failed");
            }
            toast({ title: "You're a member!", description: `${lb(res.data.balanceLb)} available now.`,
                    status: "success", duration: 4000 });
            // Fire tenant-scoped conversion pixels (no-op unless this tenant's
            // storefront host has pixels configured). Covers both the standalone
            // membership door and the membership-first join journey.
            fireMembershipConversion({
                value: Number(selected?.monthlyPrice ?? selected?.monthly_price) || 0,
                planName: selected?.name,
                transactionId: res.data?.membershipId || res.data?.enrollmentId || undefined,
            });
            onEnrolled && onEnrolled(res.data);
        } catch (err) {
            toast({ title: "Could not enroll",
                    description: err?.response?.data?.message || err.message,
                    status: "error", duration: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    if (!plans || plans.length === 0) {
        return <Text color="gray.500">No membership plans are available right now.</Text>;
    }

    return (
        <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, md: compact ? 1 : 3 }} spacing={3}>
                {plans.map((p) => {
                    const id = p.planId || p.plan_id;
                    const isSel = id === selectedId;
                    return (
                        <Box key={id} p={4} borderWidth="2px" borderRadius="lg" cursor="pointer"
                            borderColor={isSel ? "green.400" : "gray.200"}
                            bg={isSel ? "green.50" : "white"}
                            onClick={() => setSelectedId(id)}>
                            <Flex justify="space-between" align="center">
                                <Heading size="sm">{p.name}</Heading>
                                {isSel && <Badge colorScheme="green">Selected</Badge>}
                            </Flex>
                            <Text fontSize="2xl" fontWeight="bold" mt={1}>
                                {money(p.monthlyPrice ?? p.monthly_price)}
                                <Text as="span" fontSize="sm" color="gray.500"> / mo</Text>
                            </Text>
                            <Text fontSize="sm" color="gray.600">{lb(p.allotmentLb ?? p.allotment_lb)} included</Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Extra pounds {money(p.overageRate ?? p.overage_rate)}/lb
                            </Text>
                            {Number(p.beddingDiscountPct ?? p.bedding_discount_pct) > 0 && (
                                <Text fontSize="xs" color="purple.600">
                                    {Number(p.beddingDiscountPct ?? p.bedding_discount_pct)}% off bedding
                                </Text>
                            )}
                        </Box>
                    );
                })}
            </SimpleGrid>

            <Box p={3} borderWidth="1px" borderRadius="md">
                <Text fontSize="sm" mb={2}>Card on file (billed monthly, cancel anytime)</Text>
                <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                    <CardElement options={{ hidePostalCode: false }} />
                </Box>
            </Box>

            <Button colorScheme="green" onClick={handleEnroll} isLoading={submitting}
                isDisabled={!stripe}>
                Subscribe{selected ? ` — ${money(selected.monthlyPrice ?? selected.monthly_price)}/mo` : ""}
            </Button>
        </VStack>
    );
};

// ── Balance / active-member view ────────────────────────────────────────────
export const MembershipBalance = ({ laundryId, customerId, membership, ledger, onCanceled }) => {
    const toast = useToast();
    const authToken = localStorage.getItem("idToken");
    const [canceling, setCanceling] = useState(false);

    const cancel = async () => {
        setCanceling(true);
        try {
            const res = await axios.post(`${API}/api/membership/cancel`,
                { customerId, laundryId }, { headers: { "x-api-key": authToken } });
            if (res.data?.status !== "success") throw new Error(res.data?.message);
            toast({ title: "Membership canceled", status: "info", duration: 3000 });
            onCanceled && onCanceled();
        } catch (err) {
            toast({ title: "Could not cancel", description: err?.response?.data?.message || err.message,
                    status: "error", duration: 3500 });
        } finally {
            setCanceling(false);
        }
    };

    const m = membership || {};
    const balance = m.balanceLb ?? m.balance_lb;
    const next = m.nextBillingDate ?? m.next_billing_date;
    const rate = m.overageRate ?? m.overage_rate;
    // A canceled membership keeps its benefit until the paid period ends
    // (next_billing_date), so it still comes back from the API during that grace
    // window. Reflect that state instead of always showing "Active" + a Cancel
    // button — otherwise a successful cancel looks like nothing happened.
    const isCanceled = (m.status ?? m.membershipStatus) === "canceled";
    const nextDateStr = next ? new Date(next).toLocaleDateString() : null;

    return (
        <VStack align="stretch" spacing={4}>
            <Box p={5} borderWidth="1px" borderRadius="lg" bg={isCanceled ? "orange.50" : "green.50"}>
                <Flex justify="space-between" align="center">
                    <Heading size="md">{m.planName ?? m.plan_name} Membership</Heading>
                    {isCanceled
                        ? <Badge colorScheme="orange">Canceled</Badge>
                        : <Badge colorScheme="green">Active</Badge>}
                </Flex>
                <Stat mt={3}>
                    <StatLabel>Remaining this cycle</StatLabel>
                    <StatNumber>{lb(balance)}</StatNumber>
                    <StatHelpText>
                        {isCanceled
                            ? (nextDateStr
                                ? `Canceled — benefits end ${nextDateStr}. No further charges.`
                                : "Canceled. No further charges.")
                            : <>Renews {nextDateStr || "next month"} · overage {money(rate)}/lb</>}
                    </StatHelpText>
                </Stat>
            </Box>

            {ledger && ledger.length > 0 && (
                <Box>
                    <Text fontWeight="600" mb={2}>Recent activity</Text>
                    <VStack align="stretch" spacing={1}>
                        {ledger.slice(0, 8).map((e, i) => (
                            <Flex key={i} justify="space-between" fontSize="sm" color="gray.700">
                                <Text>{e.note || e.entryType || e.entry_type}</Text>
                                <Text>
                                    {Number(e.poundsDelta ?? e.pounds_delta) !== 0
                                        ? `${Number(e.poundsDelta ?? e.pounds_delta) > 0 ? "+" : ""}${Number(e.poundsDelta ?? e.pounds_delta)} lb`
                                        : ""}
                                    {Number(e.amountCharged ?? e.amount_charged) > 0
                                        ? ` ${money(e.amountCharged ?? e.amount_charged)}` : ""}
                                </Text>
                            </Flex>
                        ))}
                    </VStack>
                </Box>
            )}

            <Divider />
            {isCanceled ? (
                <Text fontSize="sm" color="gray.500">
                    Your membership is canceled and will not renew
                    {nextDateStr ? `. You can keep using your remaining balance until ${nextDateStr}.` : "."}
                </Text>
            ) : (
                <Button variant="outline" colorScheme="red" size="sm" alignSelf="flex-start"
                    onClick={cancel} isLoading={canceling}>
                    Cancel membership
                </Button>
            )}
        </VStack>
    );
};

// ── Standalone page (the SECONDARY door) ─────────────────────────────────────
const MembershipPage = (props) => {
    const params = useParams();
    const laundryId = props.laundryId || params.laundryId;
    const customerId = props.customerId || localStorage.getItem("customerId");
    const authToken = localStorage.getItem("idToken");
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    const [stripePromise, setStripePromise] = useState(null);
    const [plans, setPlans] = useState([]);
    const [membership, setMembership] = useState(null);
    const [ledger, setLedger] = useState([]);

    const loadStripeKey = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/laundry/get-info`, {
                params: { operation: "getLaundryInfo", laundryId, isCustomer: true },
                headers: { "x-api-key": authToken },
            });
            const key = res.data?.body?.stripePublicKey || res.data?.stripePublicKey;
            if (key) setStripePromise(loadStripe(key));
        } catch (err) { /* card capture simply won't render without a key */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [laundryId]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [plansRes, balRes] = await Promise.all([
                axios.get(`${API}/api/membership/plans`, { params: { laundryId, customerId } }),
                customerId
                    ? axios.get(`${API}/api/membership/balance`, { params: { laundryId, customerId } })
                    : Promise.resolve({ data: {} }),
            ]);
            setPlans(plansRes.data?.plans || []);
            const m = balRes.data?.membership || plansRes.data?.membership || null;
            setMembership(m);
            setLedger(balRes.data?.ledger || []);
        } catch (err) {
            toast({ title: "Could not load memberships", status: "error", duration: 3000 });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [laundryId, customerId]);

    useEffect(() => { if (laundryId) { loadStripeKey(); loadData(); } }, [laundryId, loadStripeKey, loadData]);

    if (loading) return <Flex justify="center" p={10}><Spinner /></Flex>;

    const isMember = !!membership;

    return (
        <Box maxW="720px" mx="auto" p={{ base: 4, md: 6 }}>
            <Heading size="lg" mb={1}>Membership</Heading>
            <Text color="gray.600" mb={5}>
                Prepaid monthly plans measured in pounds. Save on every order and roll over
                unused pounds.
            </Text>

            {isMember ? (
                <MembershipBalance
                    laundryId={laundryId} customerId={customerId}
                    membership={membership} ledger={ledger}
                    onCanceled={loadData}
                />
            ) : !customerId ? (
                <Text color="gray.500">Please sign in to subscribe.</Text>
            ) : stripePromise ? (
                <Elements stripe={stripePromise}>
                    <MembershipEnroll
                        laundryId={laundryId} customerId={customerId} plans={plans}
                        onEnrolled={loadData}
                    />
                </Elements>
            ) : (
                <Text color="gray.500">Payment is temporarily unavailable. Please try again later.</Text>
            )}
        </Box>
    );
};

export default MembershipPage;
